import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles, Typography, Button } from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import { Elements, StripeProvider } from 'react-stripe-elements';
import VoucherForm from './VoucherForm';
import ContentHeader from './ContentHeader';
import { formatPrice } from '../helpers/formatters';
import BookingPaymentForm from './BookingPaymentForm';
import { waitForObjectElement } from '../helpers/wait';

const styles = {
    root: {
        textAlign: 'left',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)'
    },
    error: {
        margin: '20px 20px 10px',
        padding: '20px',
        color: red[500],
        textAlign: 'center'
    },
    description: {
        padding: '10px',
        marginBottom: '20px',
        fontWeight: '100'
    },
    button: {
        height: '40px',
        marginTop: '10px',
        marginBottom: '10px'
    },
    passengerInfo: {
        padding: '10px',
        marginBottom: '20px',
        textAlign: 'center'
    },
    imagePreview: {
        minHeight: '56px',
        margin: '20px auto 0',
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        justifyContent: 'center',
        '& > img': {
            maxWidth: '100%'
        },
        '@media only screen and (max-device-width: 400px)': {
            margin: '0 auto 0'
        }
    },
    tripLine: {
        border: '1px solid #ccc',
        borderRadius: 5,
        marginBottom: 5,
        textAlign: 'left',
        padding: 5,
        display: 'flex'
    },
    tripLineHead: {
        display: 'flex',
        borderRight: '1px solid #ccc',
        paddingRight: 5,
        marginRight: 5,
        textAlign: 'center',
        width: 55,
        justifyContent: 'center'
    },
    tripDate: {
        fontWeight: 'bold'
    },
    tripDateDay: {
        fontSize: 20,
        display: 'block',
        color: '#5dcaff'
    },
    tripDateMonth: {
        fontSize: 8,
        display: 'block'
    },
    tripService: {
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'center',
        flexDirection: 'column'
    },
    tripStop: {
        fontSize: '0.8em',
        color: '#666',
        display: 'flex',
        alignItems: 'center'
    },
    icon: {
        fontSize: '15px'
    }
};

class ConfirmBooking extends Component {
    constructor(props) {
        super(props);

        this.state = {
            voucher: null,
            stripe: null,
            error: ''
        };
    }

    async componentDidMount() {
        const { client } = this.props;

        try {
            // Wait for stripe to be loaded
            await waitForObjectElement(window, 'Stripe');

            const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUB_KEY, {
                stripeAccount: client.stripe_account_id
            });

            this.setState({
                stripe
            });
        } catch (error) {
            console.error('Error loading Stripe:', error);
            this.setState({
                error:
                    'There was an error loading the payment system. Please try again later or contact support.'
            });
        }
    }

    setVoucher = voucher => {
        this.setState({ voucher });
    };

    netPrice = price => {
        const { voucher } = this.state;
        if (voucher) {
            return price - voucher.value;
        }
        return price;
    };

    renderTrips(bookings) {
        const { classes } = this.props;
        const sortedBookings = bookings.map(item => item);
        sortedBookings.sort((a, b) => {
            if (a.date < b.date) {
                return -1;
            }
            if (a.date > b.date) {
                return 1;
            }
            if (a.time < b.time) {
                return -1;
            }
            if (a.time > b.time) {
                return 1;
            }
            return 0;
        });

        return (
            <>
                {sortedBookings.map(item => (
                    <div className={classes.tripLine}>
                        <div className={classes.tripLineHead}>
                            <div className={classes.tripDate}>
                                <span className={classes.tripDateDay}>
                                    {moment(item.date)
                                        .format('ddd')
                                        .toUpperCase()}
                                </span>
                                <span className={classes.tripDateMonth}>
                                    {moment(item.date)
                                        .format('Do MMM')
                                        .toUpperCase()}
                                </span>
                            </div>
                        </div>
                        <div className={classes.tripService}>
                            {item.time} | {item.service_name}
                            {'stop' in item && (
                                <div className={classes.tripStop}>
                                    <span className={classes.tripStopLabel}>
                                        {item.stop}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </>
        );
    }

    render() {
        const {
            handleEdit,
            passenger,
            booking,
            client,
            imagePreviewUrl,
            classes,
            buyAgain,
            customerTicketId
        } = this.props;

        const { voucher, stripe, error } = this.state;

        if (error) {
            return (
                <div className={classes.root}>
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                </div>
            );
        }

        if (!stripe) {
            return <></>;
        }

        const { firstName, lastName } = passenger;

        return (
            <StripeProvider stripe={stripe}>
                <div>
                    <div className={classes.root}>
                        <ContentHeader
                            text="Ticket details"
                            id="ticket-details-heading"
                        />
                        {!buyAgain && (
                            <div className={classes.imagePreview}>
                                <img src={imagePreviewUrl} alt="Ticket user" />
                            </div>
                        )}
                        <div className={classes.passengerInfo}>
                            <Typography variant="body2">
                                Passenger: {firstName} {lastName}
                            </Typography>

                            <Typography variant="body2">
                                Ticket: {client.site_name} Advance Pass
                            </Typography>
                            <Typography variant="body2">
                                Price:{' '}
                                {formatPrice(
                                    this.netPrice(
                                        booking.reduce(
                                            (acc, b) => acc + Number(b.price),
                                            0
                                        ),
                                        client.features.currency
                                    )
                                )}{' '}
                                ({booking.length} journeys)
                            </Typography>
                        </div>
                    </div>
                    <div>
                        <div className={classes.root}>
                            <ContentHeader
                                text="Trips"
                                id="trip-details-heading"
                            />
                            <div className={classes.passengerInfo}>
                                {this.renderTrips(booking)}
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.button}
                                    onClick={handleEdit}
                                >
                                    Edit
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className={classes.root}>
                        <ContentHeader text="Voucher" id="voucher-heading" />
                        <VoucherForm
                            client={client}
                            setVoucher={this.setVoucher}
                        />
                    </div>
                    <div className={classes.root}>
                        <ContentHeader text="Pay" id="pay-heading" />
                        <Elements
                            fonts={[
                                {
                                    cssSrc:
                                        'https://fonts.googleapis.com/css?family=Roboto:400,500'
                                }
                            ]}
                        >
                            <BookingPaymentForm
                                booking={booking}
                                passenger={passenger}
                                client={client}
                                voucher={voucher}
                                buyAgain={buyAgain}
                                customerTicketId={customerTicketId}
                            />
                        </Elements>
                    </div>
                </div>
            </StripeProvider>
        );
    }
}

ConfirmBooking.propTypes = {
    handleEdit: PropTypes.func.isRequired,
    passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        photo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        stop: PropTypes.string
    }).isRequired,
    client: PropTypes.shape().isRequired,
    imagePreviewUrl: PropTypes.string,
    classes: PropTypes.shape().isRequired,
    buyAgain: PropTypes.bool,
    customerTicketId: PropTypes.string
};

ConfirmBooking.defaultProps = {
    imagePreviewUrl: '',
    buyAgain: false,
    customerTicketId: ''
};

export default withStyles(styles)(ConfirmBooking);
